import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { FC } from 'react';
import { PageContentHorizontalAlignment } from 'src/generated/webapp_gql';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import {
    FlaggedAlignedContentProps,
    HorizontalAlignmentProps,
    Page,
    PageProps,
} from 'src/modules/newPrograms/components/Page';
import { PageImage } from 'src/modules/newPrograms/components/PageImage';
import { ProgramPageFragment } from 'src/modules/newPrograms/documents/getProgram.generated';
import { mediaQueries } from 'src/shared/styles/variables';

const Container = styled.div<HorizontalAlignmentProps & FlaggedAlignedContentProps>(
    {
        display: 'flex',
        alignItems: 'center',
        [mediaQueries.md]: {
            flexWrap: 'wrap',
        },
    },
    ({ contentHorizontalAlignment }) => {
        switch (contentHorizontalAlignment) {
            case PageContentHorizontalAlignment.Center:
                return {
                    flexDirection: 'column-reverse',
                };
            case PageContentHorizontalAlignment.Start:
            default:
                return {
                    flexDirection: 'row',
                };
        }
    },
    ({ contentHorizontalAlignment, alignContent }) => {
        if (!alignContent) {
            return {};
        }

        switch (contentHorizontalAlignment) {
            case PageContentHorizontalAlignment.Center:
                return {
                    justifyContent: 'center',
                };
            case PageContentHorizontalAlignment.Start:
            default:
                return {
                    justifyContent: 'flex-start',
                };
        }
    },
);

const MarkdownContainer = styled.div<HorizontalAlignmentProps & FlaggedAlignedContentProps>(
    {
        minWidth: 400,
        maxWidth: 600,
        [mediaQueries.md]: {
            order: 2,
        },
    },
    ({ contentHorizontalAlignment, alignContent }) => {
        if (alignContent && contentHorizontalAlignment === PageContentHorizontalAlignment.Start) {
            return {
                [mediaQueries.lg]: {
                    minWidth: 300,
                    maxWidth: 300,
                },
            };
        }

        return {};
    },
);

interface MarkdownPageProps extends PageProps {
    page: ProgramPageFragment;
}

export const MarkdownPageImage = styled(PageImage)<
    HorizontalAlignmentProps & FlaggedAlignedContentProps
>(
    ({ alignContent, contentHorizontalAlignment }) =>
        !alignContent || contentHorizontalAlignment === PageContentHorizontalAlignment.Start
            ? {
                  marginLeft: 100,
                  [mediaQueries.md]: {
                      marginLeft: 0,
                      marginRight: 100,
                  },
              }
            : {},
    {
        objectFit: 'contain',
        objectPosition: 'center center',
    },
);

export const MarkdownPage: FC<MarkdownPageProps> = ({ page, onNavigatePrev, onNavigateNext }) => {
    const { revampedProgramsOnWeb } = useFlags();
    return (
        <Page
            onNavigatePrev={onNavigatePrev}
            onNavigateNext={onNavigateNext}
            contentVerticalAlignment={page.contentVerticalAlignment}
        >
            <Container
                alignContent={revampedProgramsOnWeb}
                contentHorizontalAlignment={page.contentHorizontalAlignment}
            >
                <MarkdownContainer
                    alignContent={revampedProgramsOnWeb}
                    contentHorizontalAlignment={page.contentHorizontalAlignment}
                >
                    <Markdown
                        textHorizontalAlignment={page.contentHorizontalAlignment}
                        text={page.content ?? ''}
                    />
                </MarkdownContainer>
                {page.image ? (
                    <MarkdownPageImage
                        width={300}
                        height={300}
                        alignContent={revampedProgramsOnWeb}
                        contentHorizontalAlignment={page.contentHorizontalAlignment}
                        src={page.image}
                    />
                ) : null}
            </Container>
        </Page>
    );
};
